import { useCallback } from 'react';
import { useToast } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { useSetRegistryCurrencyMutation } from '@graphql/mutations/currency.graphql.generated';
import { addError } from '@shared/utils/logger';
import type { CreditCardPayoutCurrencyMismatch } from './types';

type UsePayoutCurrencyMismatchArgs = Readonly<{
  eventId: Maybe<string>;
  isMismatch: boolean;
  registryCurrencyCode: Maybe<string>;
  payoutCurrencyCode: Maybe<string>;
  onSwitchClicked: (fromCurrencyCode: string, toCurrencyCode: string) => void;
  onSwitchStarted?: (toCurrencyCode: string) => void;
  onSwitchFailed?: () => void;
}>;

export const usePayoutCurrencyMismatch = ({
  eventId,
  isMismatch,
  registryCurrencyCode,
  payoutCurrencyCode,
  onSwitchClicked,
  onSwitchStarted,
  onSwitchFailed,
}: UsePayoutCurrencyMismatchArgs): CreditCardPayoutCurrencyMismatch | null => {
  const { tq } = useTranslation('sharedRegistry');
  const { toast } = useToast();
  const [setRegistryCurrency, { loading: isSwitchingRegistryCurrency }] = useSetRegistryCurrencyMutation();
  const targetCurrencyCode = payoutCurrencyCode?.toUpperCase();

  const onSwitchRegistryCurrency = useCallback(async () => {
    if (!eventId || !registryCurrencyCode || !targetCurrencyCode) return;
    onSwitchClicked(registryCurrencyCode, targetCurrencyCode);
    onSwitchStarted?.(targetCurrencyCode);
    try {
      await setRegistryCurrency({
        variables: { eventId, payload: { code: targetCurrencyCode } },
        update: (cache, { data }) => {
          const currency = data?.setRegistryCurrency;
          if (!currency) return;
          cache.modify({
            id: cache.identify({ __typename: 'EventData', id: eventId }),
            fields: { currency: (existing, { toReference }) => toReference(currency) ?? existing },
          });
        },
      });
    } catch (err) {
      onSwitchFailed?.();
      addError(err as Error, {
        context: 'switchRegistryCurrencyToPayoutCurrency',
        eventId,
        fromCurrencyCode: registryCurrencyCode,
        toCurrencyCode: targetCurrencyCode,
      });
      toast(tq`We couldn't switch your registry currency. Please try again.`);
    }
  }, [eventId, registryCurrencyCode, targetCurrencyCode, onSwitchClicked, onSwitchStarted, onSwitchFailed, setRegistryCurrency, toast, tq]);

  if (!isMismatch || !registryCurrencyCode || !targetCurrencyCode) return null;

  return {
    kind: 'payout',
    registryCurrencyCode,
    payoutCurrencyCode: targetCurrencyCode,
    onSwitchRegistryCurrency,
    isSwitchingRegistryCurrency,
  };
};
